import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import authService from '../../services/auth';

const ImageSlider = () => {
  const [images, setImages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadData, setUploadData] = useState({
    title: '',
    description: '',
    file: null
  });
  const [uploading, setUploading] = useState(false);
  const isAdmin = authService.isAdmin();

  useEffect(() => {
    fetchSliderImages();
  }, []);

  useEffect(() => {
    if (images.length > 0) {
      const interval = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [images]);

  const fetchSliderImages = async () => {
    try {
      const response = await api.get('/slider/get_images.php');
      if (response.data.success) {
        setImages(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching slider images:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('File size should be less than 5MB');
        return;
      }
      setUploadData({ ...uploadData, file });
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadData.file) {
      alert('Please select an image');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('image', uploadData.file);
    formData.append('title', uploadData.title);
    formData.append('description', uploadData.description);

    try {
      const response = await api.post('/slider/upload_image.php', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        alert('Image uploaded successfully!');
        setShowUploadForm(false);
        setShowSettings(false);
        setUploadData({ title: '', description: '', file: null });
        fetchSliderImages();
      }
    } catch (error) {
      alert('Failed to upload image: ' + (error.response?.data?.message || error.message));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) {
      return;
    }

    try {
      const response = await api.post('/slider/delete_image.php', { id: imageId });
      if (response.data.success) {
        alert('Image deleted successfully!');
        fetchSliderImages();
        if (currentIndex >= images.length - 1) {
          setCurrentIndex(0);
        }
      }
    } catch (error) {
      alert('Failed to delete image: ' + (error.response?.data?.message || error.message));
    }
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  // Function to get correct image URL based on environment
  const getImageUrl = (imagePath) => {
    // Remove leading slash if present
    const cleanPath = imagePath.startsWith('/') ? imagePath.substring(1) : imagePath;
    
    // For production (Hostinger), use absolute URL
    if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
      return `/${cleanPath}`;
    }
    
    // For local development
    return `${process.env.PUBLIC_URL}/${cleanPath}`;
  };

  if (loading) {
    return <div className="slider-loading">Loading slider...</div>;
  }

  return (
    <div className="image-slider">
      {/* Settings Button for Admin */}
      {isAdmin && (
        <button 
          className="slider-settings-btn"
          onClick={() => setShowSettings(!showSettings)}
          title="Slider Settings"
        >
          ⚙️
        </button>
      )}

      {/* Settings Panel */}
      {showSettings && isAdmin && (
        <div className="slider-settings-panel">
          <div className="settings-header">
            <h3>Slider Settings</h3>
            <button className="close-settings" onClick={() => setShowSettings(false)}>✕</button>
          </div>

          <div className="settings-content">
            <button 
              className="settings-btn add-btn"
              onClick={() => setShowUploadForm(!showUploadForm)}
            >
              ➕ Add New Slide
            </button>

            {showUploadForm && (
              <form onSubmit={handleUpload} className="slider-upload-form-compact">
                <input
                  type="text"
                  value={uploadData.title}
                  onChange={(e) => setUploadData({ ...uploadData, title: e.target.value })}
                  placeholder="Slide Title"
                  required
                />
                <input
                  type="text"
                  value={uploadData.description}
                  onChange={(e) => setUploadData({ ...uploadData, description: e.target.value })}
                  placeholder="Slide Description"
                />
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  required
                />
                <button type="submit" disabled={uploading}>
                  {uploading ? 'Uploading...' : 'Upload'}
                </button>
              </form>
            )}

            <div className="slider-list">
              <h4>Manage Slides ({images.length})</h4>
              {images.map((image, index) => (
                <div key={image.id} className="slider-list-item">
                  <span className="slide-number">{index + 1}</span>
                  <span className="slide-title">{image.title}</span>
                  <button 
                    className="delete-slide-btn"
                    onClick={() => handleDelete(image.id)}
                  >
                    🗑️
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Slider Display */}
      {images.length === 0 ? (
        <div className="slider-empty">No slider images available</div>
      ) : (
        <>
          <div className="slider-container">
            <div className="slider-content">
              {images.map((image, index) => (
                <div
                  key={image.id}
                  className={`slider-slide ${index === currentIndex ? 'active' : ''}`}
                >
                  <img
                    src={getImageUrl(image.image_path)}
                    alt={image.title || `Slide ${index + 1}`}
                    onError={(e) => {
                      console.error('Image load error:', image.image_path);
                      e.target.src = 'https://via.placeholder.com/1200x400/486670/ffffff?text=Slide+Image';
                    }}
                  />
                  <div className="slider-caption">
                    <h3>{image.title}</h3>
                    <p>{image.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="slider-dots">
            {images.map((_, index) => (
              <button
                key={index}
                className={`dot ${index === currentIndex ? 'active' : ''}`}
                onClick={() => goToSlide(index)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default ImageSlider;
